/**
 * Runner-slot resolution — the correctness core of the exposure grid.
 *
 * The grid's whole argument is that a number under the column headed "1" is the
 * home side's book in EVERY row, in every event, down the whole page. Getting
 * that wrong is not a cosmetic bug: it is an operator hedging the wrong side.
 *
 * `MarketExposure.selections` is in catalog order, which is NOT 1/X/2 order —
 * a soccer Match Odds is `['Arsenal', 'Chelsea', 'The Draw']`, so the draw sits
 * at index 2 and slotting by index would print Chelsea's book under "X". Slots
 * are therefore resolved by matching selection names against the two sides
 * parsed out of the event name, and a market we cannot PROVE is home/draw/away
 * is rendered as a named field row rather than guessed into a numbered column.
 *
 * Pure, no React, no formatting — it is unit-tested in runners.test.ts before a
 * pixel of the grid was drawn.
 */

import type { EventExposure, MarketExposure } from '@/data/types'

export type Slot = 'home' | 'draw' | 'away'

export interface RunnerCell {
  name: string
  net: number
}

export type MarketShape =
  /** Fits the numbered columns. A two-runner market has `draw: null`. */
  | { kind: 'sided'; home: RunnerCell | null; draw: RunnerCell | null; away: RunnerCell | null }
  /** Four or more runners, or a market whose sides could not be proven. */
  | { kind: 'field'; runners: RunnerCell[] }
  /** A fancy/session line — Yes/No, Over/Under. Never a numbered column. */
  | { kind: 'line'; runners: RunnerCell[] }

export interface EventSides {
  home: string
  away: string
}

/** The two names the event's `1` and `2` columns carry, plus the draw if any. */
export interface RunnerColumns {
  home: string | null
  draw: string | null
  away: string | null
}

export interface ShapedMarket {
  market: MarketExposure
  shape: MarketShape
}

export interface ShapedEvent {
  sides: EventSides | null
  columns: RunnerColumns
  /** `sided` and `field` markets, in the projection's order (worst case first). */
  rows: ShapedMarket[]
  /** Fancy/session lines, pulled out into the event's own pill block. */
  lines: ShapedMarket[]
  /** Worst case across the lines. See `fancyWorstCase` for why it is a floor. */
  fancyWorst: number
}

/** Space-v-space, case-sensitive, exactly as `src/data/catalog.ts` writes it. */
const SEPARATOR = ' v '

/** `The Draw` and `Draw`. Nothing else — a leftover does not have to be named. */
const DRAW_NAME = /^(the )?draw$/i

/** trim, collapse internal whitespace, lowercase. Nothing cleverer. */
const norm = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase()

/**
 * The two sides an event name declares, or null when it declares none.
 *
 * `'Central Delhi Kings v Purani Delhi 6'` yields both sides.
 * `'Ascot 15:40 — 1m2f Handicap'` and `'Evolution — Lightning Roulette'` do not,
 * and get the fallbacks in `shapeOf` instead of an invented pair.
 */
export function eventSides(eventName: string): EventSides | null {
  const parts = eventName.split(SEPARATOR)
  if (parts.length !== 2) return null
  const home = (parts[0] ?? '').trim()
  const away = (parts[1] ?? '').trim()
  if (!home || !away) return null
  return { home, away }
}

/**
 * Which of the three renderings a market gets, decided in a fixed order.
 *
 *  1. Fancy → `line`. TYPE WINS OVER RUNNER COUNT: an Over/Under
 *     line has exactly two selections that would slot perfectly, and slotting
 *     them would put "Over 2.5" under a column headed with a team name.
 *  2. Four or more selections → `field`. Horse racing wins, greyhound traps.
 *  3. No parseable sides → positional for two selections, and for three only
 *     when exactly one of them is literally the draw.
 *  4. Parseable sides → exact normalised name match, both sides required.
 *
 * Rule 4's failure branch is the whole point: WE DO NOT GUESS. Matching is exact
 * equality after normalisation — never fuzzy, never prefix — because
 * "Manchester Super Giants W" and "Manchester Super Giants" are two different
 * events in this catalog and a prefix match would cross them, silently drawing
 * the women's book under the men's fixture. A market that fails to prove itself
 * renders as a named field row, where every number sits beside the name it
 * belongs to.
 */
export function shapeOf(market: MarketExposure, sides: EventSides | null): MarketShape {
  const runners: RunnerCell[] = market.selections.map((s) => ({ name: s.name, net: s.net }))

  if (market.marketType === 'Fancy') {
    return { kind: 'line', runners }
  }

  if (runners.length >= 4) return { kind: 'field', runners }

  if (!sides) {
    // A live-casino `Table Round` has no fixture name to parse and two
    // selections whose order IS the market's own order; the group header row
    // prints both names, so nothing is unlabelled.
    if (runners.length === 2) {
      return { kind: 'sided', home: runners[0] ?? null, draw: null, away: runners[1] ?? null }
    }
    if (runners.length === 3) {
      const draws = runners.filter((r) => DRAW_NAME.test(r.name.trim()))
      const draw = draws.length === 1 ? draws[0] : undefined
      if (!draw) return { kind: 'field', runners }
      const others = runners.filter((r) => r !== draw)
      return { kind: 'sided', home: others[0] ?? null, draw, away: others[1] ?? null }
    }
    return { kind: 'field', runners }
  }

  const home = runners.find((r) => norm(r.name) === norm(sides.home)) ?? null
  const away = runners.find((r) => norm(r.name) === norm(sides.away)) ?? null
  // `home === away` when an event name repeats a side ("X v X"): one runner
  // cannot be both columns, and printing it twice would double-count the book.
  if (!home || !away || home === away) return { kind: 'field', runners }

  const leftovers = runners.filter((r) => r !== home && r !== away)
  if (leftovers.length === 0) return { kind: 'sided', home, draw: null, away }
  if (leftovers.length === 1) return { kind: 'sided', home, draw: leftovers[0] ?? null, away }
  return { kind: 'field', runners }
}

/**
 * Worst first, then by name.
 *
 * The name tiebreak is not decoration: without it a seven-runner race whose
 * books are all zero would reorder itself between polls, and a list that moves
 * under the cursor is a list nobody trusts.
 */
export function worstFirst(runners: RunnerCell[]): RunnerCell[] {
  return [...runners].sort((a, b) => a.net - b.net || a.name.localeCompare(b.name, 'en-IN'))
}

/**
 * The runner this row would pay on, or null when nothing on it is negative.
 * Identity-compared by the renderer, so ties resolve to the first — the same
 * one `worstFirst` puts at the head of a field strip.
 */
export function worstRunner(runners: (RunnerCell | null)[]): RunnerCell | null {
  let worst: RunnerCell | null = null
  for (const runner of runners) {
    if (!runner) continue
    if (!worst || runner.net < worst.net) worst = runner
  }
  return worst && worst.net < 0 ? worst : null
}

/**
 * The names the event's `1 / X / 2` headers carry.
 *
 * In practice every sided market in an event shares the same two teams, so this
 * is one pair. The type allows two sided markets to disagree, and if they ever
 * do the largest book wins the header — while each cell keeps its own `title`,
 * so nothing is mislabelled at the cell level even then.
 */
export function columnNames(rows: ShapedMarket[]): RunnerColumns {
  const sided = rows
    .filter((row) => row.shape.kind === 'sided')
    .sort((a, b) => b.market.matched - a.market.matched)

  const columns: RunnerColumns = { home: null, draw: null, away: null }
  for (const row of sided) {
    if (row.shape.kind !== 'sided') continue
    columns.home ??= row.shape.home?.name ?? null
    columns.draw ??= row.shape.draw?.name ?? null
    columns.away ??= row.shape.away?.name ?? null
  }
  return columns
}

/**
 * Worst case across an event's fancy lines.
 *
 * Two different pieces of arithmetic, and mixing them up is how a risk figure
 * ends up reading better than reality:
 *   · WITHIN one line, Yes and No are mutually exclusive alternatives, so the
 *     line's own worst case is `min(Yes, No)` — already computed upstream — and
 *     is never a sum across its selections.
 *   · ACROSS lines, each is a separate market that settles independently, so
 *     their worst cases add.
 * A line that cannot cost anything contributes zero rather than offsetting a
 * real liability elsewhere, which is exactly how the event roll-up in
 * `buildEventExposure` treats its markets — so this total is always a strict
 * subset of the headline it sits under.
 */
export function fancyWorstCase(lines: ShapedMarket[]): number {
  return lines.reduce((sum, line) => sum + Math.min(0, line.market.worstCase), 0)
}

/** One event, fully classified. The grid stays a renderer. */
export function shapeEvent(event: EventExposure): ShapedEvent {
  const sides = eventSides(event.eventName)
  const shaped = event.markets.map((market) => ({ market, shape: shapeOf(market, sides) }))
  const rows = shaped.filter((entry) => entry.shape.kind !== 'line')
  const lines = shaped.filter((entry) => entry.shape.kind === 'line')
  return {
    sides,
    columns: columnNames(rows),
    rows,
    lines,
    fancyWorst: fancyWorstCase(lines),
  }
}
